import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import type { SpecsValidationIssue } from "../../../service/SpecsRevisionService";
import { createStyles, palette } from "../../../styles/theme";
import { SpecsFabricsModel } from "../specsModel/SpecsFabricsModel";
import { setValueAtModelPath } from "../specsForm/modelPath";
import { getValidationIssueFrameSx } from "../specsForm/SpecsValidationIssueHighlight";
import { getSpecsIssueMatchForModelPath } from "../specsValidationIssues";
import {
  CompactColorCell,
  CompactTextCell,
} from "./FabricsSpecsTableEditorFields";

const styles = createStyles({
  tableContainer: {
    border: `1px solid ${palette.grey.lightGrey}`,
    borderRadius: 1,
    overflowX: "auto",
  },
  headCell: {
    whiteSpace: "nowrap",
    fontSize: 12,
    fontWeight: 700,
    px: 1,
    py: 1,
    bgcolor: palette.grey.lightBackground,
    borderBottom: `1px solid ${palette.grey.lightGrey}`,
  },
  bodyCell: {
    px: 1,
    py: 0.25,
    verticalAlign: "top",
    whiteSpace: "nowrap",
  },
});

export function FabricRootTable({
  model,
  validationIssues = [],
}: {
  model: SpecsFabricsModel;
  validationIssues?: SpecsValidationIssue[];
}) {
  const getCellSx = (modelPath: string) => ({
    ...styles.bodyCell,
    ...getValidationIssueFrameSx(
      getSpecsIssueMatchForModelPath({
        issues: validationIssues,
        specType: "fabric",
        modelPath,
      })
    ),
  });

  return (
    <TableContainer sx={styles.tableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {[
              "Name",
              "Group",
              "Factory",
              "Fabric Lot",
              "Text Color",
              "Description",
            ].map((label) => (
              <TableCell key={label} sx={styles.headCell}>
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={getCellSx("fabric.name")}>
              <CompactTextCell
                value={model.fabric.name}
                onChange={(nextValue) =>
                  setValueAtModelPath(model, "fabric.name", nextValue)
                }
              />
            </TableCell>
            <TableCell sx={getCellSx("fabric.group")}>
              <CompactTextCell
                value={model.fabric.group}
                onChange={(nextValue) =>
                  setValueAtModelPath(model, "fabric.group", nextValue)
                }
              />
            </TableCell>
            <TableCell sx={getCellSx("fabric.factory")}>
              <CompactTextCell
                value={model.fabric.factory}
                onChange={(nextValue) =>
                  setValueAtModelPath(model, "fabric.factory", nextValue)
                }
              />
            </TableCell>
            <TableCell sx={getCellSx("fabric.fabricLot")}>
              <CompactTextCell
                value={model.fabric.fabricLot}
                onChange={(nextValue) =>
                  setValueAtModelPath(model, "fabric.fabricLot", nextValue)
                }
              />
            </TableCell>
            <TableCell sx={getCellSx("fabric.textColor")}>
              <CompactColorCell
                value={model.fabric.textColor}
                onChange={(nextValue) =>
                  setValueAtModelPath(model, "fabric.textColor", nextValue)
                }
              />
            </TableCell>
            <TableCell sx={getCellSx("fabric.description")}>
              <CompactTextCell
                value={model.fabric.description}
                minWidth={240}
                onChange={(nextValue) =>
                  setValueAtModelPath(model, "fabric.description", nextValue)
                }
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
